/**
 * Behavior layer mounted while presentation mode is active (focus / review /
 * deliverables). Renders nothing visible by itself — it provides:
 *
 *  - Keyboard nav: Esc exits, ArrowUp/Down navigates, only in a window that
 *    shows the presentation. Capture phase with preventDefault +
 *    stopImmediatePropagation so master-layout's global Esc→stopExecution
 *    listener never fires while presenting.
 *  - Review variant: opens the active frame owner in the canonical task
 *    pocket and closes that pocket on exit without changing sidebar state.
 *  - Selection toolbar: text selection over the active presented frame's
 *    DOM node gets the same highlight/comment toolbar as the old overlays.
 *
 * Spec: docs/specs/2026-06-12-focus-review-presentation-mode.md
 */
import { useEffect, useRef } from "react";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useFramesStore } from "@/stores/frames-store";
import { isFrameHiddenByMetadata, useFrame } from "@/stores/frames-selectors";
import { useReviewModeStore } from "@/stores/review-mode-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import { useWorkspaceViewStore } from "@/stores/workspace-view-store";
import {
  captureTaskOpenIntent,
  openTaskDetails,
} from "@/lib/right-panel-navigation";
import { useTextSelection } from "@/hooks/use-text-selection";
import { SelectionToolbar } from "@/components/selection/selection-toolbar";
import { isTauri } from "@/lib/is-tauri";
import {
  exitPresentationMode,
  navigatePresentation,
} from "@/lib/presentation-mode";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { closeToCard } from "@/lib/focus/focus-card-return";
import { isFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";

/**
 * Frame types whose content is selectable text. Docs (and `note`/`markdown`,
 * their former types) have their own selection pill, so they are not here.
 */
const TEXT_FRAME_TYPES = new Set(["code", "html"]);
function selectorValue(value: string): string {
  return typeof CSS !== "undefined" && CSS.escape
    ? CSS.escape(value)
    : value.replace(/["\\]/g, "\\$&");
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return (
    !!el &&
    (el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.isContentEditable)
  );
}

export function PresentationChrome() {
  const presentation = usePresentationStore((s) => s.presentation);
  const variant = presentation?.variant;
  const selectedFrameId = useSelectionStore((s) => s.selectedFrameId);
  const desktopMode = useDesktopMode((s) => s.mode);
  const focusHiddenFrameIds = useCanvasStore((s) => s.focusHiddenFrameIds);
  // Only the canvas hides frames around a focus; the wall never does.
  const canvasView = useWorkspaceViewStore((s) => s.preferred === "canvas");
  const legacyFocusFrameId = useFramesStore((s) => {
    if (presentation || !canvasView || focusHiddenFrameIds.size === 0) {
      return undefined;
    }
    const visible = (id: string) => {
      const frame = s.framesById[id];
      return (
        !!frame &&
        !isFrameHiddenByMetadata(frame) &&
        !focusHiddenFrameIds.has(id)
      );
    };
    if (selectedFrameId && visible(selectedFrameId)) return selectedFrameId;
    return s.frameOrder.find(visible);
  });
  const legacyFocusActive = !!legacyFocusFrameId;
  const compactSelectedFrameId =
    isTauri() && desktopMode === "compact"
      ? (selectedFrameId ?? undefined)
      : undefined;
  const activeFrameId = presentation
    ? presentation.frameIds[presentation.activeIndex]
    : legacyFocusActive
      ? legacyFocusFrameId
      : compactSelectedFrameId;
  const activeData = useFrame(activeFrameId);
  const unread = useReviewModeStore((s) => s.unread);
  const recent = useReviewModeStore((s) => s.recent);

  /* ---------- Keyboard: Esc exits, arrows navigate ---------- */
  const presenting = !!presentation;
  useEffect(() => {
    if (!presenting) return;
    function onKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target) || !isFocusVisibleHere()) return;
      if (e.key === "Escape") {
        // The task pocket and the summoned chat sit above Focus and close first.
        if (
          useTaskPocketStore.getState().taskId ||
          document.querySelector("[data-task-pocket-owned]") ||
          useWorkspaceConversationOverlay.getState().overlay
        )
          return;
        e.preventDefault();
        e.stopImmediatePropagation(); // beat master-layout's Esc→stopExecution
        closeToCard(exitPresentationMode);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        navigatePresentation(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopImmediatePropagation();
        navigatePresentation(-1);
      }
    }
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [presenting]);

  /* Review uses the same task pocket as every other entry point. */
  const reviewTaskId = useRef<string | null>(null);
  const reviewOpenCurrent = useRef<(() => boolean) | null>(null);
  useEffect(() => {
    if (variant !== "review") return;
    const task = [...unread, ...recent].find(
      (item) => !!activeFrameId && item.frame_ids.includes(activeFrameId),
    );
    if (!task) return;
    useReviewModeStore
      .getState()
      .setActiveTask(task.task_id, activeFrameId ?? null);
    if (reviewTaskId.current === task.task_id) return;
    reviewTaskId.current = task.task_id;
    const shouldContinue = captureTaskOpenIntent();
    reviewOpenCurrent.current = shouldContinue;
    void openTaskDetails(task.task_id, { shouldContinue });
  }, [variant, activeFrameId, unread, recent]);
  useEffect(() => {
    if (variant !== "review") return;
    return () => {
      const pocket = useTaskPocketStore.getState();
      if (
        pocket.taskId === reviewTaskId.current ||
        reviewOpenCurrent.current?.()
      )
        pocket.close();
      reviewOpenCurrent.current = null;
      reviewTaskId.current = null;
    };
  }, [variant]);

  /* ---------- Selection toolbar over the active frame ---------- */
  const containerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    containerRef.current = activeFrameId
      ? document.querySelector<HTMLElement>(
          `[data-canvas-frame-id="${selectorValue(activeFrameId)}"]`,
        )
      : null;
  }, [activeFrameId, activeData?.frameType]);

  const frameType = activeData?.frameType;
  const isTextFrame = TEXT_FRAME_TYPES.has(frameType ?? "");
  const { selectedText, selectionRect, clearSelection } = useTextSelection({
    containerRef,
    iframeBridge: frameType === "html",
  });
  if (!activeFrameId || !activeData || !frameType) {
    return null;
  }

  return isTextFrame && selectedText && selectionRect ? (
    <SelectionToolbar
      selectedText={selectedText}
      selectionRect={selectionRect}
      frameId={activeFrameId}
      frameTitle={activeData.title || "Untitled"}
      frameType={frameType}
      onClose={clearSelection}
    />
  ) : null;
}
